import { useCallback, useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import type { BonusDefinition, PublicBoost, VoucherResponseDto } from '~/domains/promotions/types';
import { PromoCarouselLocation, VoucherType, PromotionSegmentType } from '~/domains/promotions/types';
import { parsePromotionItemsToPromoCardProps } from '~/domains/promotions/utils/transformers';
import { useGetPromotionsData } from '~/domains/promotions/hooks/useGetPromotionsData';
import { useGetMissionsData } from '~/domains/promotions/domains/gamification/domains/missions/hooks/useGetMissionsData';
import { useOpenPromoTermsModal } from '~/domains/promotions/hooks/useOpenPromoTermsModal';

import { usePromotionsContext } from '../../context/PromotionsContext';
import { useMissionModals } from '../../domains/gamification/domains/missions/hooks/useMissionModals';
import type { MissionAssignment } from '../../domains/gamification/domains/missions/types/missions.types';
import { useWhalersClub } from '../../domains/gamification/domains/whalersClub/hooks/useWhalersClub';
import WhalersClubPromoCard from '../../domains/gamification/domains/whalersClub/components/WhalersClubPromoCard/WhalersClubPromoCard';
import { PromotionActionLocation } from '../../constants/analytics-enums';


export const usePromoCarousel = (location: PromoCarouselLocation, contestId?: string) => {
  const [selectedPromoTypePill, setPromoTypePill]
    = useState<PromotionSegmentType>(PromotionSegmentType.PROMOTIONS);

  const isMissionsLocationAllowed =
    location === PromoCarouselLocation.QUICKPICKS ||
    location === PromoCarouselLocation.HOME;

  const locationAnalytics = match(location)
    .with(PromoCarouselLocation.QUICKPICKS, () => PromotionActionLocation.QP_BOARD)
    .with(PromoCarouselLocation.HOME, () => PromotionActionLocation.HOME)
    .with(PromoCarouselLocation.CONTEST, () => PromotionActionLocation.CONTEST_DETAIL)
    .otherwise(() => PromotionActionLocation.PROMO_TAB);

  const {
    items,
    totalCount,
    isLoading,
    isError,
  } = useGetPromotionsData(location, contestId);

  const {
    items: challengesActive,
    isLoading: isChallengesLoading,
    totalCount: challengesTotalCount,
  } = useGetMissionsData(
    isMissionsLocationAllowed,
    location,
    );

  const { openMissionDetailModal } = useMissionModals(location);

  const {
    tiers,
    tierStatus,
    openWhalersClubDetailModal,
    isLoading: isWhalersClubLoading,
  } = useWhalersClub();

  // The Whalers Club promo is only relevant to QuickPicks/Home surfaces — never on
  // contest carousels (contest detail, contest lobby). See EDGE-30012.
  const isWhalersClubLocationAllowed =
    location === PromoCarouselLocation.QUICKPICKS ||
    location === PromoCarouselLocation.HOME;

  const whalersClubPromoCard = useMemo(
    () =>
      tierStatus && isWhalersClubLocationAllowed ? (
        <WhalersClubPromoCard
          tiers={tiers}
          tierStatus={tierStatus}
          onClick={openWhalersClubDetailModal}
          isLoading={isWhalersClubLoading}
        />
      ) : null,
    [
      tiers,
      tierStatus,
      openWhalersClubDetailModal,
      isWhalersClubLoading,
      isWhalersClubLocationAllowed,
    ]
  );

  const {
    selectBoost,
    selectVoucher,
    selectDepositBonus,
    selectedBoost,
    selectedVoucher,
    removeBoost,
    removeVoucher,
  } = usePromotionsContext();

  // Clear incompatible bonuses when specific location is loaded
  useEffect(() => {
    match(location)
      .with(PromoCarouselLocation.QUICKPICKS, () => {
        if (
          [
            VoucherType.CONTESTS,
            VoucherType.CONTEST_ID_RESTRICTED,
            VoucherType.LEAGUES,
          ].includes(selectedVoucher?.voucherType)) removeVoucher();
      })
      .with(PromoCarouselLocation.CONTEST, () => {
        if (selectedBoost) removeBoost();
        if (selectedVoucher?.voucherType === VoucherType.QUICKPICKS) removeVoucher();
      })
  }, [location, selectedBoost, selectedVoucher]);

  // Auto-select challenges if no promotions are available
  useEffect(() => {
    if (!isLoading && totalCount < 1 && (challengesTotalCount > 0 || whalersClubPromoCard)) {
      setPromoTypePill(PromotionSegmentType.CHALLENGES);
    }
  }, [isLoading, totalCount, challengesTotalCount, whalersClubPromoCard]);

  const selectedPromoId = selectedBoost?.id ?? selectedVoucher?.id ?? null;

  const handleBoostApply = useCallback(
    (boost: PublicBoost) => selectBoost(boost, locationAnalytics),
    [selectBoost],
  );
  const handleVoucherApply = useCallback(
    (voucher: VoucherResponseDto) => selectVoucher(voucher, locationAnalytics),
    [selectVoucher],
  );
  const handleDepositBonusAction = useCallback(
    (bonus: BonusDefinition) => selectDepositBonus(bonus, locationAnalytics),
    [selectDepositBonus],
  );
  const handleChallengeClick = useCallback(
    (challenge: MissionAssignment) => {
      openMissionDetailModal(challenge);
    },
    [],
  );

  const {
    viewBoostTerms,
    viewVoucherDetails,
    viewDepositBonusDetails,
  } = useOpenPromoTermsModal({
      location: locationAnalytics,
      selectedPromoId,
  });

  const cards = useMemo(
    () => {
      const parsedCards = parsePromotionItemsToPromoCardProps({
        items,
        selectedPromoId,
        handleBoostApply,
        handleVoucherApply,
        handleDepositBonusAction,
        handleViewBoostTerms: viewBoostTerms,
        handleViewVoucherDetails: viewVoucherDetails,
        handleViewDepositBonusDetails: viewDepositBonusDetails,
      }).filter((card): card is NonNullable<typeof card> => card !== null)

      return parsedCards;
    }, [items, selectedPromoId],
  );

  return {
    cards,
    totalCount,
    isLoading,
    isError,
    selectedPromoId,
    selectedPromoTypePill,
    setPromoTypePill,
    challengesActive,
    challengesTotalCount,
    isChallengesLoading,
    handleChallengeClick,
    whalersClubPromoCard,
  };
};
